/**
 * Credit Notes and Sales Returns — the query and mutation layer.
 *
 * ONE set of hooks for BOTH types, parameterised by `docType`, because the two
 * share every screen: the same editor, the same preview, the same issue button.
 * Two parallel sets would be two copies of a form that must stay in step, and
 * the only real difference between them is the label.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getDb } from '@data/db/database';
import { DOCUMENTS_QUERY_ROOT, currentBusinessId } from '@features/documents/useDocuments';
import { currentDeviceId } from '@data/sync/deviceIdentity';
import {
  convertInvoiceToReturnDraft,
  createReturnDraft,
  deleteReturnDraft,
  issueReturnDocument,
  listReturnDocuments,
  listReturnableInvoices,
  loadInvoiceForReturn,
  loadReturnDocument,
  returnsGoods,
  updateReturnDraft,
  type ReturnDocType,
} from '@data/services/salesReturnService';

export const RETURNS_QUERY_ROOT = ['sales-returns'] as const;

/** The invoices that can still be credited or returned, for the editor's picker. */
export function useReturnableInvoices() {
  return useQuery({
    queryKey: [...RETURNS_QUERY_ROOT, 'source-invoices'],
    queryFn: async () => {
      const businessId = await currentBusinessId();
      return businessId ? listReturnableInvoices(getDb() as never, businessId) : [];
    },
  });
}

/**
 * One invoice, with what is left to return on each of its lines.
 *
 * Typed structurally rather than reusing `ReturnSource`, because the generic
 * `loadSalesOrderForDelivery` signature would put a SALES ORDER in the name of a
 * function reading an INVOICE. The editor needs the lines; nothing else does.
 */
export function useReturnSource(invoiceId: string | null, excludeDocumentId?: string) {
  return useQuery({
    queryKey: [...RETURNS_QUERY_ROOT, 'source', invoiceId, excludeDocumentId],
    enabled: invoiceId !== null,
    queryFn: async () => {
      if (!invoiceId) return null;
      return loadInvoiceForReturn(getDb() as never, invoiceId, {
        excludeDocumentId,
      });
    },
  });
}

export function useReturnDocuments(docType: ReturnDocType) {
  return useQuery({
    queryKey: [...RETURNS_QUERY_ROOT, 'list', docType],
    queryFn: async () => {
      const businessId = await currentBusinessId();
      return businessId
        ? listReturnDocuments(getDb() as never, businessId, docType)
        : {
            documents: [],
            total: 0,
            truncated: false,
            partyNamesByDocumentId: new Map<string, string>(),
          };
    },
  });
}

export function useReturnDocument(documentId: string | null) {
  return useQuery({
    queryKey: [...RETURNS_QUERY_ROOT, 'document', documentId],
    enabled: documentId !== null,
    queryFn: () => (documentId ? loadReturnDocument(getDb() as never, documentId) : null),
  });
}

async function invalidateReturnData(client: ReturnType<typeof useQueryClient>) {
  await Promise.all([
    client.invalidateQueries({ queryKey: DOCUMENTS_QUERY_ROOT }),
    client.invalidateQueries({ queryKey: RETURNS_QUERY_ROOT }),
  ]);
}

/** Create a draft from an invoice, for the given document type. */
export function useCreateReturn(docType: ReturnDocType) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (invoiceId: string) =>
      convertInvoiceToReturnDraft(getDb() as never, { docType, invoiceId }),
    onSuccess: () => invalidateReturnData(client),
  });
}

/** Create a draft with explicit lines, bypassing the "everything remaining" default. */
export function useCreateReturnFromLines() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (input: Parameters<typeof createReturnDraft>[1]) => {
      const db = getDb();
      return createReturnDraft(db as never, { ...input, deviceId: await currentDeviceId(db) });
    },
    onSuccess: () => invalidateReturnData(client),
  });
}

export function useUpdateReturn() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: Parameters<typeof updateReturnDraft>[2] }) => {
      const db = getDb();
      return currentDeviceId(db).then((deviceId) =>
        updateReturnDraft(db as never, id, { ...input, deviceId }),
      );
    },
    onSuccess: () => invalidateReturnData(client),
  });
}

export function useDeleteReturn() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => {
      const db = getDb();
      return currentDeviceId(db).then((deviceId) =>
        deleteReturnDraft(db as never, id, { deviceId }),
      );
    },
    onSuccess: () => invalidateReturnData(client),
  });
}

/**
 * Issue, then settle the source invoice.
 *
 * NOT the shared `useIssueDocument`: that wrapper owns the stock movement but
 * knows nothing about the invoice's balance, and issuing a return without
 * settling the invoice would leave the customer owing money that was credited
 * back. Both happen in one transaction inside the service.
 */
export function useIssueReturn() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (documentId: string) => {
      const db = getDb();
      return currentDeviceId(db).then((deviceId) =>
        issueReturnDocument(db as never, documentId, { deviceId }),
      );
    },
    onSuccess: () => invalidateReturnData(client),
  });
}

export { returnsGoods };